/**
 * Engineering — the GitHub-Issues-backed component surface.
 *
 * Wire shape for GET /api/engineering. Unlike every other Command Center
 * component, Engineering is NOT backed by Airtable: its work items are the
 * lf2.0 GitHub issues in chrisfnrose/LegalFlare-Engineering (ADR-0014's hybrid
 * backend pattern; the component was reserved at platform design and is the
 * first GitHub-backed surface to land — see ADR-0109).
 *
 * Write surface (ADR-0110, supersedes ADR-0109's deferral): Command Center is
 * where engineering work is captured, not just viewed — the page can
 * create an issue (the inflow), close one (mark done → it drops off the open
 * list), and pin
 * one to the top. Pin is the manual float (ADR-0110): an issue carrying the
 * `surfaced` label sorts above the rest. Pin state is derivable from the
 * issue's own `labels[]`, so it adds no field to the wire — the page checks
 * `labels.includes(ENGINEERING_PINNED_LABEL)`.
 *
 * `configured` is the not-connected sentinel. The worker needs a GITHUB_TOKEN
 * secret to read the (private) repo; until it's provisioned the route returns
 * `configured: false` with an empty list (a 200, NOT an error) so the page
 * shows a clean "connect the tracker" state instead of a red failure. Once the
 * token lands, `configured: true` and `issues` carries the open set.
 */

import { z } from "zod";

/** GitHub issue state. The list view requests `open`; the field is carried so
 *  a future all/closed filter doesn't reshape the wire. */
export const EngineeringIssueStateSchema = z.enum(["open", "closed"]);

export const EngineeringIssueSchema = z.object({
  /** GitHub issue number — the #N operators cite. */
  number: z.number().int().positive(),
  title: z.string(),
  state: EngineeringIssueStateSchema,
  /** github.com html URL — the row links out here to act. */
  url: z.string(),
  /** Label names (e.g. "lf2.0", "bug"). The backlog is organised by label. */
  labels: z.array(z.string()),
  /** Assignee login handles; empty when unassigned. */
  assignees: z.array(z.string()),
  /** Comment count — a cheap "is there discussion" signal. */
  comments: z.number().int().nonnegative(),
  /** ISO-8601 timestamps straight from GitHub. */
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const EngineeringResponseSchema = z.object({
  /** False until a GITHUB_TOKEN secret is provisioned — see file header. */
  configured: z.boolean(),
  /** "owner/name" of the tracker repo. */
  repo: z.string(),
  /** The label the surface filters on (the lf2.0 cut of the cabinet's issues). */
  label: z.string(),
  /** Open issues carrying `label`, newest-updated first. Empty when
   *  unconfigured or genuinely none open. */
  issues: z.array(EngineeringIssueSchema),
});

export type EngineeringIssueState = z.infer<typeof EngineeringIssueStateSchema>;
export type EngineeringIssue = z.infer<typeof EngineeringIssueSchema>;
export type EngineeringResponse = z.infer<typeof EngineeringResponseSchema>;

// ---------------------------------------------------------------------------
// Write surface (ADR-0110)
// ---------------------------------------------------------------------------

/** The label that floats an issue to the top of the Engineering surface. Set
 *  and cleared by the pin toggle; the page derives `isPinned` from an issue's
 *  `labels[]` rather than carrying a separate wire field. Manual float in v1 —
 *  the operator decides what's hot (no automatic ranking). */
export const ENGINEERING_PINNED_LABEL = "surfaced";

/** Create an engineering issue from Command Center. The route auto-stamps the
 *  surface's `lf2.0` label (an issue created without it would not appear in the
 *  filtered list), so the caller supplies only the human content. Body is
 *  optional — a one-line title is a valid task. */
export const CreateIssueRequestSchema = z.object({
  /** Issue title — required, trimmed non-empty. */
  title: z.string().trim().min(1, "title is required"),
  /** Markdown body — optional. */
  body: z.string().optional(),
});

/** Toggle the pin (the `surfaced` label) on an issue. `pinned: true` adds it,
 *  `false` removes it. */
export const PinIssueRequestSchema = z.object({
  pinned: z.boolean(),
});

export type CreateIssueRequest = z.infer<typeof CreateIssueRequestSchema>;
export type PinIssueRequest = z.infer<typeof PinIssueRequestSchema>;
